import { inject, Injectable } from '@angular/core';
import { from, map, Observable } from 'rxjs';

import { PlatformFeatures, SDK } from '@plugins/middleware-sdk';
import { SingleOrArray } from '@plugins/shared/types';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';

export type PlatformKey = keyof PlatformFeatures;
type CheckReturns<Keys extends string> = { [key in Keys]: PlatformKey[] };
type AvailabilityKeys = 'available' | 'unavailable';
type AccessibilityKeys = 'enabled' | 'disabled';
export type AvailabilityReturn = CheckReturns<AvailabilityKeys>;
export type EnabledReturn = CheckReturns<AccessibilityKeys | AvailabilityKeys>;

/** Provides information if requrested platform features are
 * available and / or accessible (enabled).
 *
 * Availability and accessibility checks are cached. Caches are clearable. */
@Injectable({ providedIn: 'root' })
export class FeatureCheckService {
  protected sdk = inject(SDK);
  protected availabilityFeaturesChecked = new Set<PlatformKey>();
  protected enabledFeaturesChecked = new Set<PlatformKey>();
  protected availabilityMaps: Record<AvailabilityKeys, Set<PlatformKey>> = {
    available: new Set(),
    unavailable: new Set(),
  };
  protected accessibilityMaps: Record<AccessibilityKeys, Set<PlatformKey>> = {
    enabled: new Set(),
    disabled: new Set(),
  };

  featuresAvailability(
    features: SingleOrArray<PlatformKey>,
  ): Observable<AvailabilityReturn> {
    return from(this.performAvailabilityCheck(features)).pipe(
      map(() => ({
        available: this.filterFeatures(
          this.availabilityMaps.available,
          features,
        ),
        unavailable: this.filterFeatures(
          this.availabilityMaps.unavailable,
          features,
        ),
      })),
    );
  }

  featuresEnabled(
    features: SingleOrArray<PlatformKey>,
  ): Observable<EnabledReturn> {
    return from(this.performEnabledCheck(features)).pipe(
      map(() => ({
        enabled: this.filterFeatures(this.accessibilityMaps.enabled, features),
        disabled: this.filterFeatures(
          this.accessibilityMaps.disabled,
          features,
        ),
        available: this.filterFeatures(
          this.availabilityMaps.available,
          features,
        ),
        unavailable: this.filterFeatures(
          this.availabilityMaps.unavailable,
          features,
        ),
      })),
    );
  }

  isFeatureAvailable(feature: PlatformKey): Observable<boolean> {
    return this.featuresAvailability(feature).pipe(
      map(({ available }) => available.includes(feature)),
    );
  }

  isFeatureUnavailable(feature: PlatformKey): Observable<boolean> {
    return this.isFeatureAvailable(feature).pipe(
      map((available) => !available),
    );
  }

  isFeatureEnabled(feature: PlatformKey): Observable<boolean> {
    return this.featuresEnabled(feature).pipe(
      map(({ enabled }) => enabled.includes(feature)),
    );
  }

  isFeatureDisabled(feature: PlatformKey): Observable<boolean> {
    return this.isFeatureEnabled(feature).pipe(map((enabled) => !enabled));
  }

  clearCache(): void {
    this.availabilityFeaturesChecked.clear();
    this.enabledFeaturesChecked.clear();
    [this.accessibilityMaps, this.availabilityMaps]
      .map<Set<PlatformKey>[]>((obj) => Object.values(obj))
      .flat()
      .forEach((val) => {
        val.clear();
      });
  }

  protected async performAvailabilityCheck(
    features: SingleOrArray<PlatformKey>,
  ) {
    for (const feature of returnAsArray(features)) {
      if (this.availabilityFeaturesChecked.has(feature)) continue;
      const isAvailable = await this.sdk.isFeatureAvailable(feature);
      if (isAvailable) {
        this.availabilityMaps.available.add(feature);
      } else {
        this.availabilityMaps.unavailable.add(feature);
      }
      this.availabilityFeaturesChecked.add(feature);
    }
  }

  protected async performEnabledCheck(features: SingleOrArray<PlatformKey>) {
    const arrFeatures = returnAsArray(features);
    // Availability check is a precondition for an accessbility check
    const uncheckedAvailability = arrFeatures.filter(
      (feature) => !this.availabilityFeaturesChecked.has(feature),
    );

    if (uncheckedAvailability.length) {
      await this.performAvailabilityCheck(uncheckedAvailability);
    }
    for (const feature of arrFeatures) {
      if (this.enabledFeaturesChecked.has(feature)) continue;
      if (this.availabilityMaps.unavailable.has(feature)) {
        this.accessibilityMaps.disabled.add(feature);
        continue;
      }
      if (await this.sdk.isFeatureEnabled(feature)) {
        this.accessibilityMaps.enabled.add(feature);
      } else {
        this.accessibilityMaps.disabled.add(feature);
      }
      this.enabledFeaturesChecked.add(feature);
    }
  }

  protected filterFeatures<T extends PlatformKey>(
    source: Set<T>,
    targets: SingleOrArray<T>,
  ) {
    const targetsArray = returnAsArray(targets);
    return [...source].filter((feature) => targetsArray.includes(feature));
  }
}
